import React, { Fragment, ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { useBookingFormPassengerSections } from "shared/hooks/bookingForm/bookingFormPassengerSections";
import { BookingFormPassengerSection } from "shared/types";

import Img from "@components/FileServer/Img";
import PassengerModal from "@components/Profile/Modals/PassengerModal";
import Modal from "@components/UI/Modal";
import Table from "@components/UI/Table";
import FullName from "@components/Utils/FullName";

type BookingFormPassengersProps = {
  bookingFormPassengerSection: BookingFormPassengerSection;
};

function BookingFormPassenger({
  bookingFormPassengerSection,
}: BookingFormPassengersProps): ReactElement {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [passengerModalOpened, setPassengerModalOpened] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  const refetchData = () => setDataVersion((v) => v + 1);
  const { isLoggedIn, frontendSettings, defaultCountry } = useSelector(
    (state: AppState) => ({
      isLoggedIn: state.user.isLoggedIn,
      frontendSettings: state.storage.frontendSettings,
      defaultCountry:
        state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry,
    })
  );

  const intl = useIntl();
  const isNotCbtEnabled =
    frontendSettings.dealerCorporateSettings?.enableCbt === "false";

  const {
    setTravelersFieldsFromProfile,
    passengers,
  } = useBookingFormPassengerSections({
    bookingFormPassengerSection,
    dataVersion,
  });

  const handleSetTravelersFieldsFromProfile = (traveler) => {
    setTravelersFieldsFromProfile(traveler);
    setShowModal(false);
  };

  return (
    <>
      {isLoggedIn && isNotCbtEnabled && (
        <>
          <div
            id={`${Number(
              bookingFormPassengerSection.elements?.[0].match(/\d+/)[0]
            )}-result-action-btn`}
            className="result-action-btn"
            onClick={() => setShowModal(true)}
            role="button"
          >
            <FormattedMessage id="Result.fromProfile" />
          </div>
          <Modal
            className="passenger_modal"
            isActive={showModal && !passengerModalOpened}
            onClose={() => setShowModal(false)}
          >
            {passengers?.length > 0 ? (
              <div className="main__body">
                <div className="main__title">
                  <FormattedMessage id="Profile.travelers" />
                </div>
                <Table
                  rows={passengers.map((p, index) => [
                    <Fragment key={index}>
                      {p.isOwner && (
                        <Img
                          src="/static/images/ico-signin.svg"
                          className="header-signin-image--small"
                          alt="sign in icon"
                        />
                      )}
                      <FullName person={p} intl={intl} />
                    </Fragment>,
                    p.email,
                    p.telephone,
                  ])}
                  data={passengers}
                  action={(traveler) =>
                    handleSetTravelersFieldsFromProfile(traveler)
                  }
                  className="travelers"
                />
                <div
                  role="button"
                  className="fake-link add-traveler"
                  onClick={() => setPassengerModalOpened(true)}
                >
                  <FormattedMessage id="Profile.addTraveler" />
                </div>
              </div>
            ) : (
              <div className="profile-main__card--empty">
                <Img src="/static/images/travelers-card.svg" />
                <div className="profile-main__card-desc">
                  <FormattedMessage id="Profile.noTravelers" />
                </div>
                <div
                  role="button"
                  className="fake-link"
                  onClick={() => setPassengerModalOpened(true)}
                >
                  <FormattedMessage id="Profile.addTraveler" />
                </div>
              </div>
            )}
          </Modal>
          {passengerModalOpened && (
            <PassengerModal
              show={passengerModalOpened}
              onClose={() => {
                setPassengerModalOpened(false);
                refetchData();
              }}
              defaultCountry={defaultCountry}
              passengerForEdit={null}
            />
          )}
        </>
      )}
    </>
  );
}

export default BookingFormPassenger;
